import { FC } from 'react';
import { createUseStyles } from 'react-jss';
import {
    describeUnreadable,
    IClientBaseline,
    movedTheirCondition,
    STANDING_LABEL,
    clientDisplayName
} from '@innerhive-internal/innerhive-common';
import { BRAND, CHART_INK, STANDING_COLOR } from './agencyChartTheme';
import { useInstance } from '@meraki-internal/react-dependency-injection';
import { PersonAvatar } from '../../avatar/Avatars';
import { OrganizationInsightsState } from '../OrganizationInsightsState';

const MAX_ROWS = 8;

const useStyles = createUseStyles({
    rows: {
        display: 'flex',
        flexDirection: 'column' as const,
        gap: 8
    },

    row: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        width: '100%',
        background: 'none',
        border: 0,
        borderRadius: 8,
        padding: '4px 6px',
        font: 'inherit',
        textAlign: 'left' as const,
        cursor: 'pointer',

        '&:hover': {
            background: BRAND.tealWash
        }
    },

    who: {
        display: 'flex',
        alignItems: 'center',
        gap: 9,
        flex: '0 0 34%',
        minWidth: 0
    },

    name: {
        minWidth: 0,
        overflow: 'hidden',
        textOverflow: 'ellipsis',
        whiteSpace: 'nowrap' as const,
        fontSize: 13.5,
        color: BRAND.deepTeal,
        fontWeight: 500
    },

    track: {
        position: 'relative',
        flex: 1,
        height: 18
    },

    centreLine: {
        position: 'absolute',
        left: '50%',
        top: 0,
        bottom: 0,
        width: 1,
        background: CHART_INK.axis
    },

    bar: {
        position: 'absolute',
        top: 3,
        height: 12
    },

    value: {
        flex: '0 0 62px',
        fontSize: 12.5,
        textAlign: 'right' as const,
        color: CHART_INK.label,
        fontVariantNumeric: 'tabular-nums'
    },

    scaleRow: {
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        marginTop: 10,
        fontSize: 11.5,
        color: CHART_INK.label
    },

    scaleSpacer: {
        flex: '0 0 34%'
    },

    scaleTrack: {
        flex: 1,
        display: 'flex',
        justifyContent: 'space-between'
    },

    scaleValueSpacer: {
        flex: '0 0 62px'
    },

    emptyState: {
        fontSize: 13,
        color: CHART_INK.label,
        lineHeight: 1.5
    },

    more: {
        marginTop: 10,
        fontSize: 12.5,
        color: CHART_INK.label
    }
});

/**
 * Deliberately carries no client name. A title attribute is recorded verbatim by session replay and
 * no class can mask an attribute, so a caseload's worth of names would end up in replay. The name is
 * already on the row, where mp-mask does reach it.
 */
const hoverText = (baseline: IClientBaseline): string => {
    const { recentPerWeek, baselinePerWeek } = baseline.volume;
    return `${STANDING_LABEL[baseline.standing]}, with ${recentPerWeek} ${recentPerWeek === 1 ? 'finding' : 'findings'} a week against ${baselinePerWeek} before`;
};

/** What the figure on each row actually counts. */
export const DEVIATION_EXPLAINER =
    'The bar is the change in what has been raised about this client, against their own earlier weeks '
    + 'rather than anyone else. Each part of daily life counts once, by the most serious thing raised '
    + 'about it: 3 for something that happened, 2 for something that stood out, 1 for anything else, '
    + 'and a step less when the finding was unsure. So +2 could be a part of life with nothing raised '
    + 'before now having something that stood out. A family recording more about the same things reads '
    + 'the same. A family recording much less raises less, so a bar toward Settling is worth checking '
    + 'against what was written. Open a row for the notes behind it.';

const formatChange = (value: number): string => {
    const rounded = Math.round(value * 10) / 10;
    return `${rounded > 0 ? '+' : ''}${rounded}`;
};

/**
 * What the strip says when it has no bars to draw, which is several different situations.
 *
 * Deliberately short and about the chart. The BeaSays line rendered directly beneath this strip
 * (see InsightsFocusLens) explains the same state in prose, so these two strings sit adjacent on
 * screen and must stay complementary rather than both reaching for the fullest sentence. Saying
 * the same thing twice in one card reads as a rendering fault.
 */
const nothingMovingMessage = (params: {
    baselines: IClientBaseline[];
    narrowed?: boolean;
    unreadableCount: number;
}): string => {
    const { baselines, narrowed, unreadableCount } = params;

    // Nothing selected is not the same as nothing measured, and saying the second about the first
    // invents a history problem the caseload does not have. A book whose every client was
    // unreadable is a third thing again: there are clients, they just could not be read.
    if (baselines.length === 0) {
        if (narrowed) {
            return 'No clients in this selection.';
        }
        return unreadableCount > 0
            ? 'No bars: none of the clients on the book could be read.'
            : 'No clients on the book yet.';
    }
    if (!baselines.some(baseline => baseline.conditionStanding !== 'unknown')) {
        return 'No bars yet: nobody has enough to compare against.';
    }

    // A client kept off the strip is still a client somebody has to see, so an empty strip must
    // not read as nothing worth looking at. This says nothing about the rest of their record on
    // purpose: the group includes a client with less raised than usual, for whom "nothing moved" is
    // as untrue as "more raised".
    const withheld = baselines.filter(baseline => baseline.hasRecentIncident && !movedTheirCondition(baseline)).length;
    if (withheld > 0) {
        return `No bars this month. ${withheld === 1 ? 'One client is' : `${withheld} clients are`} on the attention list instead.`;
    }

    if (narrowed) {
        return 'No bars for this selection.';
    }
    return `No bars this month: everybody held their own baseline.${describeUnreadable(unreadableCount)}`;
};

/**
 * Each client against their own recent normal, not against each other.
 *
 * This is the part of the tab that a fixed threshold cannot do. A client carrying four findings a
 * week may be entirely as expected, and a client who has gone from none to one may be the visit
 * worth making today. Ranking the caseload by volume would put the first at the top and bury the
 * second; ranking it by movement against each client's own baseline puts the right one in front of
 * the dispatcher.
 *
 * Diverging from a centre line, which is each client's own baseline. A client sitting on it is left
 * out of the strip entirely rather than drawn as a zero-length bar: "unchanged" is not a result in
 * either direction, and a row of them would push the movement off the bottom of the card.
 */
export const BaselineDeviationStrip: FC<{
    baselines: IClientBaseline[];
    /** Whether the rows have already been narrowed, which changes what an empty strip means. */
    narrowed?: boolean;
    /** Clients missing from the strip because they could not be read, so "everybody" can say so. */
    unreadableCount: number;
    onSelectClient: (careMapId: string) => void;
}> = ({ baselines, narrowed, unreadableCount, onSelectClient }) => {
    const classes = useStyles();
    const insightsState = useInstance(OrganizationInsightsState);

    // Each bar is what has been raised about a client against their own earlier weeks, so membership
    // is decided on the condition. movedTheirCondition is shared with the sentence rendered beneath
    // this strip, because the two are one judgement rendered twice and nothing else keeps them in step.
    const allMoving = baselines
        .filter(movedTheirCondition)
        .sort((a, b) => Math.abs(b.condition.change) - Math.abs(a.condition.change));
    const moving = allMoving.slice(0, MAX_ROWS);
    const unlisted = allMoving.length - moving.length;

    if (moving.length === 0) {
        return (
            <div className={classes.emptyState} data-id="baseline-strip-nothing-moving">
                {nothingMovingMessage({ baselines, narrowed, unreadableCount })}
            </div>
        );
    }

    // The widest movement sets the scale, so the strip always fills its width and small real changes
    // stay legible instead of collapsing into slivers next to one big one.
    // Floored, not just maximised. Every row here cleared the tolerance so the widest is never
    // actually zero, but dividing by it is one filter change away from rendering width: NaN%.
    const widest = Math.max(...moving.map(baseline => Math.abs(baseline.condition.change)), 0.1);

    return (
        <div data-id="baseline-deviation-strip">
            <div className={classes.rows}>
                {moving.map(baseline => {
                    const colour = STANDING_COLOR[baseline.standing];
                    const extent = (Math.abs(baseline.condition.change) / widest) * 50;
                    const isAbove = baseline.condition.change > 0;

                    return (
                        <button
                            key={baseline.careMapId}
                            type="button"
                            className={classes.row}
                            data-id="baseline-row"
                            data-standing={baseline.standing}
                            onClick={() => onSelectClient(baseline.careMapId)}
                            title={hoverText(baseline)}
                        >
                            <span className={classes.who}>
                                <PersonAvatar size={26} identity={insightsState.getClientAvatar(baseline.careMapId)} name={baseline.clientName} />
                                <span className={`${classes.name} mp-mask`}>{clientDisplayName(baseline.clientName)}</span>
                            </span>

                            <span className={classes.track}>
                                <span className={classes.centreLine} />
                                <span
                                    className={classes.bar}
                                    style={{
                                        background: colour,
                                        left: isAbove ? '50%' : `${50 - extent}%`,
                                        width: `${extent}%`,
                                        // Round only the growing end, so the bar reads as anchored to the centre.
                                        borderRadius: isAbove ? '0 4px 4px 0' : '4px 0 0 4px'
                                    }}
                                />
                            </span>

                            <span className={classes.value}>{formatChange(baseline.condition.change)}</span>
                        </button>
                    );
                })}
            </div>

            <div className={classes.scaleRow}>
                <span className={classes.scaleSpacer} />
                <span className={classes.scaleTrack}>
                    <span>Settling</span>
                    <span>Baseline</span>
                    <span>{STANDING_LABEL.above}</span>
                </span>
                <span className={classes.scaleValueSpacer} />
            </div>

            {/* Said out loud rather than left to the reader to notice: a dispatcher reading this for
                who to see today would otherwise miss clients the honeycomb still shows. */}
            {unlisted > 0 && (
                <div className={classes.more} data-id="baseline-strip-more">
                    {unlisted} more {unlisted === 1 ? 'client has' : 'clients have'} moved by less than these.
                </div>
            )}
        </div>
    );
};
